import { useState, useRef, useEffect, useCallback, FormEvent } from 'react';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import styles from './NewUploadDialog.module.css';

export interface NewUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onResolved: (orderId: string) => void;
}

type Mode = 'shopify' | 'backfill' | 'test';

/**
 * Validate an order number input string.
 * Returns null if valid, or an error message string if invalid.
 * Valid: non-empty after trim, alphanumeric + hyphens only, max 64 chars.
 */
export function validateOrderNumber(input: string): string | null {
  const trimmed = input.trim();
  if (trimmed.length === 0) {
    return 'Order number is required.';
  }
  if (trimmed.length > 64) {
    return 'Order number must be 64 characters or fewer.';
  }
  if (!/^[a-zA-Z0-9-]+$/.test(trimmed)) {
    return 'Order number can only contain letters, numbers, and hyphens.';
  }
  return null;
}

/**
 * Validate an order number for the backfill form, where a single leading `#` is
 * allowed. Shopify's order name carries the store prefix, so a synced record
 * holds "#CDL1014" — a backfilled one is typed by hand and has to be able to
 * match. Mirrors `validate_backfill_order_number` in the admin handler.
 */
export function validateBackfillOrderNumber(input: string): string | null {
  const trimmed = input.trim();
  return validateOrderNumber(trimmed.startsWith('#') ? trimmed.slice(1) : trimmed);
}

/**
 * Validate a Shopify numeric identifier. Must be bare digits: these become the
 * order record's keys, and a `gid://` URI would file it where nothing else looks.
 */
export function validateShopifyId(input: string, label: string): string | null {
  const trimmed = input.trim();
  if (trimmed.length === 0) {
    return `${label} is required.`;
  }
  if (!/^[0-9]+$/.test(trimmed)) {
    return `${label} must be the numeric ID only (no gid:// prefix).`;
  }
  return null;
}

/**
 * Convert the `yyyy-mm-dd` value of a date input into the RFC 3339 timestamp the
 * API expects. The time is not asked for: `shopify_order_date` drives display
 * and sort order only, so the date is what matters, and midnight UTC keeps the
 * order on the day it was actually placed.
 */
export function orderDateToRfc3339(value: string): string {
  return `${value}T00:00:00Z`;
}

export function NewUploadDialog({ open, onClose, onResolved }: NewUploadDialogProps) {
  const [mode, setMode] = useState<Mode>('shopify');
  const [orderNumber, setOrderNumber] = useState('');
  // Test-order fields.
  const [testLabel, setTestLabel] = useState('');
  const [testName, setTestName] = useState('');
  const [testEmail, setTestEmail] = useState('');
  // Backfill fields — every value the resolve path would have read from Shopify.
  const [backfillNumber, setBackfillNumber] = useState('');
  const [backfillOrderId, setBackfillOrderId] = useState('');
  const [backfillCustomerId, setBackfillCustomerId] = useState('');
  const [backfillEmail, setBackfillEmail] = useState('');
  const [backfillName, setBackfillName] = useState('');
  const [backfillDate, setBackfillDate] = useState('');
  const [backfillFulfilment, setBackfillFulfilment] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { handleUnauthorized } = useAdminAuth();

  // Focus the first field on open within 200ms
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Escape key handler
  useEffect(() => {
    if (!open) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setMode('shopify');
      setOrderNumber('');
      setTestLabel('');
      setTestName('');
      setTestEmail('');
      setBackfillNumber('');
      setBackfillOrderId('');
      setBackfillCustomerId('');
      setBackfillEmail('');
      setBackfillName('');
      setBackfillDate('');
      setBackfillFulfilment('');
      setIsLoading(false);
      setError(null);
      setValidationError(null);
    }
  }, [open]);

  const handleOrderNumberChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderNumber(e.target.value);
    setValidationError(null);
    setError(null);
  }, []);

  const switchMode = useCallback((next: Mode) => {
    setMode(next);
    setError(null);
    setValidationError(null);
  }, []);

  const submitShopify = useCallback(async () => {
    const trimmed = orderNumber.trim();
    const validationResult = validateOrderNumber(trimmed);
    if (validationResult) {
      setValidationError(validationResult);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const res = await fetch('/api/v1/admin/orders/resolve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ order_number: trimmed }),
      });

      if (res.ok) {
        const data = await res.json();
        onResolved(data.order_id);
        return;
      }

      if (res.status === 401 || res.status === 403) {
        handleUnauthorized();
        return;
      }

      switch (res.status) {
        case 404:
          setError('Order not found in Shopify. Check the order number and try again.');
          break;
        case 502:
          setError('Unable to look up order. Please try again later.');
          break;
        default:
          setError('An unexpected error occurred. Please try again.');
          break;
      }
    } catch {
      setError('Network error. Check your connection.');
    } finally {
      setIsLoading(false);
    }
  }, [orderNumber, onResolved, handleUnauthorized]);

  const submitTest = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const res = await fetch('/api/v1/admin/orders/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          label: testLabel.trim() || undefined,
          customer_name: testName.trim() || undefined,
          customer_email: testEmail.trim() || undefined,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        onResolved(data.order_id);
        return;
      }

      if (res.status === 401 || res.status === 403) {
        handleUnauthorized();
        return;
      }

      if (res.status === 400) {
        const data = await res.json().catch(() => null);
        setError(data?.error ?? 'Please check the details and try again.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } catch {
      setError('Network error. Check your connection.');
    } finally {
      setIsLoading(false);
    }
  }, [testLabel, testName, testEmail, onResolved, handleUnauthorized]);

  const submitBackfill = useCallback(async () => {
    const numberError = validateBackfillOrderNumber(backfillNumber);
    const orderIdError = validateShopifyId(backfillOrderId, 'Shopify order ID');
    const customerIdError = validateShopifyId(backfillCustomerId, 'Customer ID');
    const firstError =
      numberError ??
      orderIdError ??
      customerIdError ??
      (backfillEmail.trim().length === 0 ? 'Customer email is required.' : null) ??
      (backfillDate.length === 0 ? 'Order date is required.' : null);

    if (firstError) {
      setValidationError(firstError);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const res = await fetch('/api/v1/admin/orders/backfill', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          order_number: backfillNumber.trim(),
          shopify_order_id: backfillOrderId.trim(),
          customer_id: backfillCustomerId.trim(),
          customer_email: backfillEmail.trim(),
          order_date: orderDateToRfc3339(backfillDate),
          customer_name: backfillName.trim() || undefined,
          // Omitted rather than sent empty: the API reads a missing status as
          // Shopify's null, which means unfulfilled.
          fulfillment_status: backfillFulfilment || undefined,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        onResolved(data.order_id);
        return;
      }

      // The backfill endpoint itself never answers 403 — one here is the
      // authoriser rejecting the admin session, same as everywhere else.
      if (res.status === 401 || res.status === 403) {
        handleUnauthorized();
        return;
      }

      // 400 and 409 both carry a specific, actionable message (a bad field, or an
      // order already filed under another customer) — show it rather than a
      // generic failure the admin cannot act on.
      if (res.status === 400 || res.status === 409) {
        const data = await res.json().catch(() => null);
        setError(data?.error ?? 'Please check the details and try again.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } catch {
      setError('Network error. Check your connection.');
    } finally {
      setIsLoading(false);
    }
  }, [
    backfillNumber,
    backfillOrderId,
    backfillCustomerId,
    backfillEmail,
    backfillName,
    backfillDate,
    backfillFulfilment,
    onResolved,
    handleUnauthorized,
  ]);

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      if (mode === 'shopify') {
        void submitShopify();
      } else if (mode === 'backfill') {
        void submitBackfill();
      } else {
        void submitTest();
      }
    },
    [mode, submitShopify, submitBackfill, submitTest],
  );

  if (!open) return null;

  const isSubmitDisabled =
    isLoading || (mode === 'shopify' && orderNumber.trim().length === 0);

  const SUBMIT_LABELS: Record<Mode, { idle: string; busy: string }> = {
    shopify: { idle: 'Submit', busy: 'Resolving...' },
    backfill: { idle: 'Add Order', busy: 'Adding...' },
    test: { idle: 'Create Test Order', busy: 'Creating...' },
  };
  const submitLabel = isLoading ? SUBMIT_LABELS[mode].busy : SUBMIT_LABELS[mode].idle;

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-label="New Upload">
      <div className={styles.dialog}>
        <h2 className={styles.title}>New Upload</h2>

        <div className={styles.modeToggle} role="tablist" aria-label="Order type">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'shopify'}
            className={`${styles.modeButton} ${mode === 'shopify' ? styles.modeButtonActive : ''}`}
            onClick={() => switchMode('shopify')}
            disabled={isLoading}
          >
            Shopify order
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'backfill'}
            className={`${styles.modeButton} ${mode === 'backfill' ? styles.modeButtonActive : ''}`}
            onClick={() => switchMode('backfill')}
            disabled={isLoading}
          >
            Older order
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'test'}
            className={`${styles.modeButton} ${mode === 'test' ? styles.modeButtonActive : ''}`}
            onClick={() => switchMode('test')}
            disabled={isLoading}
          >
            Test order
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {mode === 'shopify' && (
            <>
              <label htmlFor="order-number-input" className={styles.label}>
                Order Number
              </label>
              <input
                id="order-number-input"
                ref={inputRef}
                type="text"
                className={styles.input}
                value={orderNumber}
                onChange={handleOrderNumberChange}
                placeholder="e.g. CDL1112"
                maxLength={64}
                disabled={isLoading}
                aria-invalid={!!validationError}
                aria-describedby={validationError ? 'validation-error' : undefined}
              />
            </>
          )}

          {mode === 'backfill' && (
            <>
              <p className={styles.hint}>
                For an order Shopify won&apos;t return — its API only exposes the last 60
                days. Copy the details from the order&apos;s page in the Shopify admin. If
                Shopify does still have the order, its values are used instead of these.
              </p>

              <label htmlFor="backfill-number-input" className={styles.label}>
                Order Number
              </label>
              <input
                id="backfill-number-input"
                ref={inputRef}
                type="text"
                className={styles.input}
                value={backfillNumber}
                onChange={(e) => {
                  setBackfillNumber(e.target.value);
                  setValidationError(null);
                  setError(null);
                }}
                placeholder="e.g. #CDL1014"
                maxLength={64}
                disabled={isLoading}
              />

              <label htmlFor="backfill-order-id-input" className={styles.label}>
                Shopify Order ID
              </label>
              <input
                id="backfill-order-id-input"
                type="text"
                inputMode="numeric"
                className={styles.input}
                value={backfillOrderId}
                onChange={(e) => {
                  setBackfillOrderId(e.target.value);
                  setValidationError(null);
                  setError(null);
                }}
                placeholder="the digits at the end of the order's admin URL"
                disabled={isLoading}
              />

              <label htmlFor="backfill-customer-id-input" className={styles.label}>
                Customer ID
              </label>
              <input
                id="backfill-customer-id-input"
                type="text"
                inputMode="numeric"
                className={styles.input}
                value={backfillCustomerId}
                onChange={(e) => {
                  setBackfillCustomerId(e.target.value);
                  setValidationError(null);
                  setError(null);
                }}
                placeholder="the digits at the end of the customer's admin URL"
                disabled={isLoading}
              />

              <label htmlFor="backfill-email-input" className={styles.label}>
                Customer Email
              </label>
              <input
                id="backfill-email-input"
                type="email"
                className={styles.input}
                value={backfillEmail}
                onChange={(e) => {
                  setBackfillEmail(e.target.value);
                  setValidationError(null);
                  setError(null);
                }}
                placeholder="e.g. customer@example.com"
                maxLength={254}
                disabled={isLoading}
              />

              <label htmlFor="backfill-name-input" className={styles.label}>
                Customer Name <span className={styles.optional}>(optional)</span>
              </label>
              <input
                id="backfill-name-input"
                type="text"
                className={styles.input}
                value={backfillName}
                onChange={(e) => {
                  setBackfillName(e.target.value);
                  setError(null);
                }}
                placeholder="e.g. Annie Livermore"
                disabled={isLoading}
              />

              <label htmlFor="backfill-date-input" className={styles.label}>
                Order Date
              </label>
              <input
                id="backfill-date-input"
                type="date"
                className={styles.input}
                value={backfillDate}
                onChange={(e) => {
                  setBackfillDate(e.target.value);
                  setValidationError(null);
                  setError(null);
                }}
                disabled={isLoading}
              />

              <label htmlFor="backfill-fulfilment-input" className={styles.label}>
                Fulfilment <span className={styles.optional}>(optional)</span>
              </label>
              <select
                id="backfill-fulfilment-input"
                className={styles.input}
                value={backfillFulfilment}
                onChange={(e) => {
                  setBackfillFulfilment(e.target.value);
                  setError(null);
                }}
                disabled={isLoading}
              >
                <option value="">Unfulfilled</option>
                <option value="partial">Partial</option>
                <option value="fulfilled">Fulfilled</option>
              </select>
            </>
          )}

          {mode === 'test' && (
            <>
              <p className={styles.hint}>
                Creates a local-only demo order (not linked to Shopify) for showing off the
                customer experience.
              </p>

              <label htmlFor="test-label-input" className={styles.label}>
                Label <span className={styles.optional}>(optional)</span>
              </label>
              <input
                id="test-label-input"
                ref={inputRef}
                type="text"
                className={styles.input}
                value={testLabel}
                onChange={(e) => {
                  setTestLabel(e.target.value);
                  setError(null);
                }}
                placeholder="e.g. DEMO-STUDIO"
                maxLength={64}
                disabled={isLoading}
              />

              <label htmlFor="test-name-input" className={styles.label}>
                Customer Name <span className={styles.optional}>(optional)</span>
              </label>
              <input
                id="test-name-input"
                type="text"
                className={styles.input}
                value={testName}
                onChange={(e) => {
                  setTestName(e.target.value);
                  setError(null);
                }}
                placeholder="e.g. Demo Customer"
                disabled={isLoading}
              />

              <label htmlFor="test-email-input" className={styles.label}>
                Customer Email <span className={styles.optional}>(optional)</span>
              </label>
              <input
                id="test-email-input"
                type="email"
                className={styles.input}
                value={testEmail}
                onChange={(e) => {
                  setTestEmail(e.target.value);
                  setError(null);
                }}
                placeholder="e.g. demo@example.com"
                maxLength={254}
                disabled={isLoading}
              />
            </>
          )}

          {validationError && (
            <p id="validation-error" className={styles.validationError} role="alert">
              {validationError}
            </p>
          )}

          {error && (
            <p className={styles.apiError} role="alert">
              {error}
            </p>
          )}

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button type="submit" className={styles.submitButton} disabled={isSubmitDisabled}>
              {submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
